import { FeedbackError } from '@/components/Feedback';
import { useModals } from '@/hooks/ModalManager';
import { EventStatus } from '@/modules/core/domain/interfaces';
import { changeTaskStatus } from '@/screens/registrations/Tasks/services/changeTaskStatus';
import { startTaskEvent } from '@/screens/registrations/Tasks/services/startTaskEvent';
import { openInNewTab } from '@/utils/openInNewTab';
import { Button, HStack, Icon, IconButton, VStack } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';

import { formatTaskData, useChecklistSummary } from '../../useChecklistSummary';

type TaskActionsProps = {
  onClose: () => void;
  openChecklist?: () => void;
  disabled?: boolean;
};

const TaskActions: React.FC<TaskActionsProps> = ({
  openChecklist,
  disabled = false,
}) => {
  const {
    data,
    isLoading,
    handleStart,
    continueChecklist,
    handleFinish,
  } = useChecklistSummary();
  const { t } = useTranslation();
  const modals = useModals();

  const handleStartTask = async (task: any) => {
    const formatedData = formatTaskData(task);
    try {
      await changeTaskStatus(formatedData, 'draft');
      const res = await startTaskEvent(formatedData);
      openInNewTab(`${window.location.origin}/external/event/${res.id}?autoEdit=true`);
    } catch (e) {
      FeedbackError({ mainText: 'Unexpected Error' });
    }
  };

  if (!data) return null;

  if (data.status === 'done') {
    return (
      <Button
        isLoading={isLoading}
        onClick={openChecklist}
        colorScheme="purple"
        w="full"
      >
        {t('buttons.view_report')}
      </Button>
    );
  }

  if (!data.event) {
    return (
      <HStack w="full">
        <Button
          disabled={disabled || isLoading}
          isLoading={isLoading}
          colorScheme="green"
          w="full"
          onClick={handleStart}
        >
          {t('buttons.start_checklist')}
        </Button>
        <IconButton
          onClick={() => handleStartTask(data)}
          variant="outline"
          aria-label="external-link"
          colorScheme={disabled ? '' : 'green'}
          icon={<Icon as={FiExternalLink} fontSize="17px" />}
          disabled={disabled}
        />
      </HStack>
    );
  }

  switch (data.event?.status) {
    case EventStatus.PENDING:
      return (
        <HStack w="full">
          <Button
            disabled={disabled || isLoading}
            isLoading={isLoading}
            colorScheme="green"
            w="full"
            onClick={handleStart}
          >
            {t('buttons.start_checklist')}
          </Button>
          <IconButton
            onClick={() => handleStartTask(data)}
            variant="outline"
            aria-label="external-link"
            colorScheme={disabled ? '' : 'green'}
            icon={<Icon as={FiExternalLink} fontSize="17px" />}
            disabled={disabled}
          />
        </HStack>
      );

    case EventStatus.DRAFT:
      return (
        <Button
          disabled={disabled || isLoading}
          isLoading={isLoading}
          onClick={continueChecklist}
          colorScheme="orange"
          w="full"
        >
          {t('buttons.resume_checklist')}
        </Button>
      );

    case EventStatus.DONE:
      return (
        <VStack w="full">
          <Button
            disabled={disabled || isLoading}
            isLoading={isLoading}
            onClick={continueChecklist}
            colorScheme="purple"
            variant="outline"
            w="full"
          >
            {t('buttons.review_checklist')}
          </Button>
          <Button
            disabled={disabled || isLoading}
            isLoading={isLoading}
            onClick={() => {
              modals.confirm({
                title: 'Deseja finalizar esta tarefa?',
                body: 'Esta ação é irreversível.',
                confirmLabel: 'Sim',
                cancelLabel: 'Continuar editando',
                onConfirm: handleFinish,
              });
            }}
            colorScheme="green"
            w="full"
          >
            {t('buttons.finish_checklist')}
          </Button>
        </VStack>
      );
    default:
      return null;
  }
};

export default TaskActions;
